<template>
  <div>
    <label :class="$style.label">
      <input
        v-model="enableManagedUID"
        :class="$style.inputPosition"
        :disabled="isRestrictedField('enable_managed_uid')"
        name="Enforce UID/GID Consistency"
        type="checkbox"
      >
      Enforce UID/GID consistency for all systems
    </label>
    <IconTooltip
      :iconClass="['jc-info-circle', $style.icon]"
    >
      <p>
        {{ $options.tooltip.uidgid.text }}
      </p>
      <div class="read-more-container">
        <a
          class="read-more-text-link"
          :href="$options.tooltip.uidgid.kbLink"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{ $options.tooltip.uidgid.kbLinkText }}
        </a>
        <a
          class="read-more-icon-link"
          :href="$options.tooltip.uidgid.kbLink"
          rel="noopener noreferrer"
          target="_blank"
        >
          <i class="jc-tool-kb read-more-icon" />
        </a>
      </div>
    </IconTooltip>
    <div :class="$style.inputRow">
      <InputValidator
        :class="$style.half"
        :config="{
          autocomplete: 'off',
          attribute: 'unix_uid',
          disabled: isRestrictedField('unix_uid'),
          id: 'unixUID',
          type: 'number',
          label:'Unix UID',
          name: 'Unix UID',
          validate: validateRulesForUIDGID,
          value: unixUID,
        }"
        @InputValidatorFocus="handleModalWarning"
        @InputValidatorModel="valueChange"
      />
      <InputValidator
        :class="$style.half"
        :config="{
          attribute: 'unix_guid',
          disabled: isRestrictedField('unix_guid'),
          id: 'unixGID',
          type: 'number',
          label:'Unix GID',
          name: 'Unix GID',
          autocomplete: 'off',
          validate: validateRulesForUIDGID,
          value: unixGID,
        }"
        @InputValidatorFocus="handleModalWarning"
        @InputValidatorModel="valueChange"
      />
    </div>
    <Modal
      v-model="modalVisible"
      :cancelText="modalCancelText"
      :handleMainAction="handleModalWarning"
      :headerText="modalHeaderText"
      :mainActionText="modalActionText"
    >
      <p>
        {{ modalWarningText }}
      </p>
    </Modal>
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import IconTooltip from 'components/Tooltip/IconTooltip.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import Modal from 'client/components/Modal/Modal.vue';

const storeModel = StoreModelMap[ItemTypeConsts.systemUser];

export default {
  name: 'UIDAndGIDConsistency',

  components: {
    InputValidator,
    Modal,
    IconTooltip,
  },

  inject: ['$validator'],

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  props: {
    enableManagedUIDForUser: {
      type: Boolean,
      default: false,
    },
    handleValueChange: {
      type: Function,
      required: true,
    },
    unixGID: {
      type: [Number, String],
      default: '',
    },
    unixUID: {
      type: [Number, String],
      default: '',
    },
  },

  data: () => ({
    modalActionText: 'continue',
    modalCancelText: 'cancel',
    modalHeaderText: 'Important Note',
    modalVisible: false,
    modalSeen: false,
    modalWarningText: 'Changing a user’s UID or GID may result in files and/or processes left on one or more servers without an associated user account. You may need to manually reset the ownership of these files to re-associate them with the correct user account. Any processes running under this user‘s UID or GID may need to be restarted.',
  }),

  tooltip: {
    uidgid: {
      text: 'When checked, users will be assigned this UID/GID on systems to which they are bound. Once checked, this setting cannot be unchecked. You’ll be notified of UID/GID conflicts in the notification panel and will need to resolve them manually.',
      kbLink: KbArticleUrls.configurationAlerts,
      kbLinkText: 'View Conflict Notification Knowledgebase Article',
    },
  },

  computed: {
    ...mapGetters(storeModel, ['isRestrictedField']),

    enableManagedUID: {
      get() {
        return this.enableManagedUIDForUser;
      },
      set(checkedState) {
        return this.handleValueChange(
          checkedState,
          'enable_managed_uid',
        );
      },
    },
    inlineErrorMessages() {
      const errorObj = {
        custom: {
          'Unix GID': {},
          'Unix UID': {},
        },
      };

      const required = 'must be a valid number higher than zero';
      const numeric = 'must be a valid number';
      const minValue = 'must be a valid number higher than zero';

      Object.keys(errorObj.custom).forEach((item) => {
        errorObj.custom[item].required = `${item} ${required}`;
        errorObj.custom[item].numeric = `${item} ${numeric}`;
        errorObj.custom[item].min_value = `${item} ${minValue}`;
      });

      return errorObj;
    },

    validateRulesForUIDGID() {
      const rules = {
        min_value: 1,
        numeric: true,
      };

      if (this.enableManagedUID === true) {
        rules.required = true;
      } else {
        rules.required = false;
      }

      return rules;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  mounted() {
    this.$validator.localize('en', this.inlineErrorMessages);
  },

  methods: {
    handleModalWarning(targetValue, attribute, name, args = []) {
      const [, $event] = args;
      const UID = this.$props.unixUID || '';
      const GID = this.$props.unixGID || '';

      if (UID.length < 1 && GID.length < 1) {
        this.modalVisible = false;
        this.modalSeen = true;
        return;
      }

      if (this.modalSeen === true) {
        this.modalVisible = false;
        return;
      }

      if (this.modalVisible === true) {
        this.modalVisible = false;
        this.modalSeen = true;
        $event.target.focus();
        return;
      }

      this.modalSeen = true;
      this.modalVisible = true;
    },

    valueChange(value, key) {
      this.handleValueChange(value, key);
    },
  },
};
</script>

<style module>
@import '../../components/FormValidation/css/formFields.css';
@import '../../css/flex.css';

@value (fontHeight) from '~components/FormValidation/css/formFields.css';

.icon {
  font-size: fontHeight;
}
</style>
